/* @flow */
import * as React from 'react';
import invariant from 'invariant';
import cx from 'classnames';

import Table from 'components/Table.jsx';
import TableRow from 'components/TableRow.jsx';
import { getSortableHeader, getHeader } from './generators/Header.jsx';

import type { IconName } from 'components/Icon.jsx';

import getField from './ComposableTableField.jsx';
import type { Field } from './ComposableTableField.jsx';

import './styles/ComposableTable.scss';

type ColumnHeaderIcon = {
  iconName: IconName,
  tooltip: string,
};

export type ColumnHeader = {
  name: string,
  sortValue?: string,
  icon?: ColumnHeaderIcon,
};

type Props = {
  columnHeaders?: ColumnHeader[],
  columnGenerators: Function[],
  rows: Object[],
  keyField: Field,
  rightAlignedLastColumn?: boolean,
  handleSort?: Function,
  handleRowClick?: Function,
  loading?: boolean,
};

export type SortDirection = 'asc' | 'desc';

type State = {
  sortColumn: ?string,
  sortDirection: ?SortDirection,
};

class ComposableTable extends React.Component<Props, State> {
  state: State = {
    sortColumn: null,
    sortDirection: null,
  };

  generateHeaders = (columnHeaders: ColumnHeader[]): Array<React.Node> => {
    const { sortColumn, sortDirection } = this.state;

    return columnHeaders.map(header => {
      if (header.sortValue) {
        return getSortableHeader(header, sortColumn, sortDirection, this.handleSort);
      } else {
        return getHeader(header);
      }
    });
  };

  handleSort = (sortValue: string, sortDirection: SortDirection) => {
    this.setState({ sortColumn: sortValue, sortDirection });

    if (this.props.handleSort) {
      this.props.handleSort(sortValue, sortDirection);
    }
  };

  buildRow = (row: Object) => {
    return this.props.columnGenerators.map((generator, index) => {
      const key = index;
      return <td key={key}>{generator(row)}</td>;
    });
  };

  renderTableBody = (keyField: Field, rows: Object[]) => {
    const { handleRowClick } = this.props;

    return rows.map(row => (
      <TableRow
        hoverable={Boolean(handleRowClick) || Boolean(row.onClick)}
        onClick={() => {
          if (handleRowClick) {
            handleRowClick(row);
          }
          if (row.onClick) {
            row.onClick();
          }
        }}
        key={getField(row, keyField)}>
        {this.buildRow(row)}
      </TableRow>
    ));
  };

  render() {
    const {
      columnGenerators,
      columnHeaders,
      keyField,
      handleSort,
      rightAlignedLastColumn,
      rows,
      loading,
    } = this.props;

    if (columnHeaders) {
      invariant(
        columnGenerators.length === columnHeaders.length,
        "Won't have the same number of headers as columns!"
      );
      invariant(
        columnHeaders.some(header => header.sortValue) ? handleSort : true,
        'Missing sortable function for sorting columns'
      );
    }

    return (
      <React.Fragment>
        <Table
          className={cx(
            'ComposableTable',
            rightAlignedLastColumn && 'ComposableTable--rightAlignedLastColumn'
          )}
          head={columnHeaders ? <tr>{this.generateHeaders(columnHeaders)}</tr> : null}>
          <tbody>{!loading && rows && this.renderTableBody(keyField, rows)}</tbody>
        </Table>
      </React.Fragment>
    );
  }
}

export default ComposableTable;
